import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Hook para limpar parâmetros da URL relacionados a um DataTable quando o componente for desmontado
 * @param tableKey - Chave única do DataTable
 */
export const useDataTableCleanup = (tableKey?: string) => {
  const [, setSearchParams] = useSearchParams();
  const tableKeyRef = useRef(tableKey);

  // Atualiza a ref quando tableKey muda
  useEffect(() => {
    tableKeyRef.current = tableKey;
  }, [tableKey]);

  useEffect(() => {
    // Cleanup function que executa APENAS quando o componente é desmontado
    return () => {
      const currentTableKey = tableKeyRef.current;
      if (!currentTableKey) return;

      // Captura os parâmetros atuais da URL no momento do desmonte
      const currentParams = new URLSearchParams(window.location.search);
      let hasChanges = false;

      // Lista de padrões de parâmetros para remover
      const patternsToRemove = [
        `filter_${currentTableKey}`,
        `page_${currentTableKey}`,
        `pageSize_${currentTableKey}`,
        `sort_${currentTableKey}`,
        `direction_${currentTableKey}`,
        `search_${currentTableKey}`,
      ];

      // Remove parâmetros que começam com o tableKey ou seguem os padrões
      const keysToRemove: string[] = [];
      currentParams.forEach((value, key) => {
        const shouldRemove = patternsToRemove.some(
          (pattern) => key === pattern || key.startsWith(`${currentTableKey}_`),
        );

        if (shouldRemove) {
          keysToRemove.push(key);
          hasChanges = true;
        }
      });

      // Remove as chaves encontradas
      keysToRemove.forEach((key) => {
        currentParams.delete(key);
      });

      // Atualiza os searchParams se houve mudanças
      if (hasChanges) {
        // Usa setSearchParams de forma assíncrona para evitar problemas durante o desmonte
        setTimeout(() => {
          setSearchParams(currentParams, { replace: true });
        }, 0);
      }
    };
  }, []); // Array de dependências vazio - executa apenas na montagem/desmontagem
};
